import { useSetBusinessPersonData } from '@/context/BusinessPersonData/Provider';
import {
  useDefaultBusinessPersonData,
  useLazyTpmInvoices,
  useSetParseValidityDate,
} from '@/context/services/tpm/hooks';
import { BusinessPersonData } from '@/context/services/tpm/type';
import { createBusinessPersonData } from '@/context/services/tpm/utils';
import { isSuccess } from '@/context/services/type';
import { useTranslation } from '@/i18n';
import { tpmResponseHasMessage } from '@/libs/typeguard/isError';
import { useCallback, useRef } from 'react';
import {
  FieldPath,
  FieldValues,
  Path,
  PathValue,
  useFormContext,
} from 'react-hook-form';

type SuccessTPM = {
  error?: never;
  message?: never;
  value: BusinessPersonData;
};

type ErrorValidate = {
  error: 'validate';
  message?: never;
  value: BusinessPersonData;
};

type ErrorTPM =
  | {
      error: true | 'message';
      message: string;
      value: BusinessPersonData;
    }
  | ErrorValidate;

type ResponseTPM = SuccessTPM | ErrorTPM;

export const useGetTPM = () => {
  const { fetch, isLoading } = useLazyTpmInvoices();
  const setParseValidityDate = useSetParseValidityDate();
  const defaultBusinessPersonData = useDefaultBusinessPersonData();
  const { t } = useTranslation();

  const DEFAULT_ERROR_MESSAGE = t(
    'invoice_registration_number_unexpected_error'
  );

  const getTPM = useCallback(
    async (number: string | null | undefined): Promise<ResponseTPM> => {
      if (!number || number.length !== 13) {
        return {
          error: 'validate',
          value: setParseValidityDate(defaultBusinessPersonData),
        };
      }

      const resp = await fetch(number);

      if (!isSuccess(resp.status)) {
        if (tpmResponseHasMessage(resp.data)) {
          return {
            error: true,
            message: resp.data.message || DEFAULT_ERROR_MESSAGE,
            value: setParseValidityDate(defaultBusinessPersonData),
          };
        } else {
          return {
            error: 'message',
            message: DEFAULT_ERROR_MESSAGE,
            value: setParseValidityDate(defaultBusinessPersonData),
          };
        }
      } else {
        const data = resp.data;
        const businessPersonBase = createBusinessPersonData(data, false);
        const value = setParseValidityDate(businessPersonBase);

        return {
          value,
        };
      }
    },
    [
      fetch,
      setParseValidityDate,
      defaultBusinessPersonData,
      DEFAULT_ERROR_MESSAGE,
    ]
  );
  return {
    getTPM,
    isLoading,
  };
};

type CallGetTPMResponse = {
  result: true | { error: true; message: string } | string;
  value: BusinessPersonData;
};

export const useInput13Text = () => {
  const setContextBusinessPersonData = useSetBusinessPersonData();
  const defaultBusinessPersonData = useDefaultBusinessPersonData();
  const businessPersonDataRef = useRef(defaultBusinessPersonData);
  const updateContextBusinessPersonData = useCallback(() => {
    setContextBusinessPersonData(businessPersonDataRef.current);
    return businessPersonDataRef.current;
  }, [setContextBusinessPersonData]);

  const { getTPM, isLoading } = useGetTPM();
  const callGetTPM = useCallback(
    async (number: string | null | undefined): Promise<CallGetTPMResponse> => {
      const resp = await getTPM(number);
      if (resp.error === 'validate') {
        return {
          result: true,
          value: resp.value,
        };
      }

      if (resp.error) {
        if (resp.error === true) {
          return {
            result: {
              error: true,
              message: resp.message,
            },
            value: resp.value,
          };
        } else {
          return {
            result: resp.message,
            value: resp.value,
          };
        }
      } else {
        return {
          result: true,
          value: resp.value,
        };
      }
    },
    [getTPM]
  );
  const callBackAfterInput13text = useCallback(
    async (number: string | null | undefined) => {
      const resp = await callGetTPM(number);
      businessPersonDataRef.current = resp.value;
      if (resp.result === true) {
        return resp.result;
      } else if (typeof resp.result === 'string') {
        return resp.result;
      } else {
        return resp.result;
      }
    },
    [callGetTPM]
  );

  return {
    callGetTPM,
    callBackAfterInput13text,
    updateContextBusinessPersonData,
    isLoading,
  };
};

export const useInvoiceRegistrationNumberAndSetBusinessPersonDataHooks = () => {
  const {
    callBackAfterInput13text,
    updateContextBusinessPersonData,
    isLoading,
  } = useInput13Text();

  const callBackAfterInput13TextWarningType = useCallback(
    async (number: string | null | undefined) => {
      const result = await callBackAfterInput13text(number);
      if (typeof result === 'string') {
        return result;
      } else if (typeof result === 'boolean' && result) {
        return true;
      } else {
        return result.message;
      }
    },
    [callBackAfterInput13text]
  );

  return {
    updateContextBusinessPersonData,
    callBackAfterInput13TextWarningType,
    isLoading,
  };
};

export const useUpdateInvoiceRegistrationNumberAndSetBusinessPersonData = <
  TFieldValues extends FieldValues
>() => {
  const { callBackAfterInput13text, updateContextBusinessPersonData } =
    useInput13Text();
  const { setValue } = useFormContext<TFieldValues>();
  return useCallback(
    async (
      name: FieldPath<TFieldValues> | undefined,
      value: string | null | undefined
    ) => {
      if (name) {
        setValue(
          name,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          value as PathValue<TFieldValues, Path<TFieldValues>>
        );
        await callBackAfterInput13text(value);
        return updateContextBusinessPersonData();
      }
    },
    [callBackAfterInput13text, setValue, updateContextBusinessPersonData]
  );
};
